import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Wand2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { AddToPlaylist } from "@/components/music/LibraryTab";

const BATCH = 60;
const label = (t) => (t.artist || t.album || t.genre ? [t.title, t.artist && `by ${t.artist}`, t.album && `album ${t.album}`, t.genre, t.year].filter(Boolean).join(", ") : t.path.replace(/\.[^.]+$/, ""));
export const moodPaths = (m, mood) => m.tracks.filter((t) => m.moods[t.path]?.includes(mood)).map((t) => t.path);

const Suggest = ({ m, getSceneText, mix }) => {
  const [busy, setBusy] = useState(false);
  const [s, setS] = useState(null);
  const run = async () => {
    const text = getSceneText?.() || "";
    if (text.length < 20) return toast.info("Write a few lines first, then I can read the mood");
    setBusy(true);
    try { setS((await api.post("/music/suggest-mood", { text, custom: m.customMoods })).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!getSceneText) return null;
  const Opt = ({ mood, testid }) => {
    const n = moodPaths(m, mood).length;
    return <button onClick={() => mix(mood)} disabled={!n} className="rounded-full bg-[#D97706] px-2.5 py-0.5 text-[11px] font-medium text-[#0B0D10] disabled:bg-[#2D3442] disabled:text-[#7A7468]" data-testid={testid}>Play {mood}{n ? ` · ${n}` : " (no songs yet)"}</button>;
  };
  return (
    <div className="space-y-2 rounded border border-[#2D3442] p-2.5" data-testid="mood-suggest">
      <button onClick={run} disabled={busy} className="flex w-full items-center justify-center gap-1.5 text-xs text-[#F59E0B] hover:underline disabled:opacity-60" data-testid="mood-suggest-button">
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />}{busy ? "Reading your scene…" : "Suggest mood from my scene"}
      </button>
      {s && <div className="space-y-1.5 text-center animate-in fade-in duration-300" data-testid="mood-suggest-result">
        <p className="text-xs text-[#E8DFD1]">This scene feels <b className="text-[#FBBF24]" data-testid="mood-suggest-mood">{s.mood}</b>{s.reason && <span className="text-[#A8A090]"> — {s.reason}</span>}</p>
        <div className="flex flex-wrap justify-center gap-1.5"><Opt mood={s.mood} testid="mood-suggest-play" />{s.alt && <Opt mood={s.alt} testid="mood-suggest-play-alt" />}</div>
      </div>}
    </div>
  );
};

const NewMood = ({ m }) => {
  const [name, setName] = useState("");
  const add = () => { const v = m.moodOps.create(name); if (v) { toast.success(`Added the “${v}” mood. Tag songs with it from the Library.`); setName(""); } };
  return (
    <div className="flex gap-1.5">
      <input value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} maxLength={30} placeholder="Create a mood, e.g. Tavern Night" className="min-w-0 flex-1 rounded border border-[#2D3442] bg-transparent px-2 py-1 text-[11px] text-[#F3EBDD]" data-testid="music-mood-new-input" />
      <button onClick={add} disabled={!name.trim()} className="rounded bg-[#D97706] px-2 text-[11px] font-medium text-[#0B0D10] disabled:opacity-40" data-testid="music-mood-new-add">Add</button>
    </div>
  );
};

export const MoodTab = ({ m, getSceneText }) => {
  const [busy, setBusy] = useState(null);
  const untagged = m.tracks.filter((t) => !m.moods[t.path]);
  const count = (mood) => m.tracks.filter((t) => m.moods[t.path]?.includes(mood)).length;
  const sort = async () => {
    const todo = untagged.slice(0, 600);
    setBusy({ done: 0, total: todo.length });
    try {
      for (let i = 0; i < todo.length; i += BATCH) {
        const chunk = todo.slice(i, i + BATCH);
        const { data } = await api.post("/music/moods", { tracks: chunk.map((t, j) => ({ id: String(j), name: label(t).slice(0, 300) })), custom: m.customMoods });
        const add = Object.fromEntries(Object.entries(data.moods).map(([j, ms]) => [chunk[Number(j)].path, ms]));
        chunk.forEach((t) => { if (!add[t.path]) add[t.path] = []; });
        m.setMoods((old) => ({ ...old, ...add }));
        setBusy({ done: Math.min(todo.length, i + BATCH), total: todo.length });
      }
      toast.success("Your library is sorted by mood");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const mix = (mood) => {
    const paths = moodPaths(m, mood);
    if (!paths.length) return toast.info(`No ${mood.toLowerCase()} songs found yet`);
    m.play(paths, null, `${mood} mix`, { mood });
  };
  return (
    <div className="space-y-3" data-testid="music-mood">
      <Suggest m={m} getSceneText={getSceneText} mix={mix} />
      <p className="text-[11px] leading-relaxed text-[#A8A090]">Pick the feeling you're writing toward and LoreRight plays songs from your library that match it.</p>
      {untagged.length > 0 && (
        <button onClick={sort} disabled={!!busy} className="flex w-full items-center justify-center gap-1.5 rounded border border-[#D97706]/50 py-1.5 text-xs text-[#F59E0B] transition-colors hover:bg-[#D97706]/10 disabled:opacity-60" data-testid="music-mood-sort">
          {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" />Listening… {busy.done}/{busy.total}</> : <><Wand2 className="h-3.5 w-3.5" />Sort {Math.min(untagged.length, 600)} {untagged.length === 1 ? "song" : "songs"} by mood</>}
        </button>
      )}
      <div className="grid grid-cols-2 gap-1.5">
        {m.allMoods.map((mood) => (
          <div key={mood} className={`flex items-center rounded border text-xs transition-colors ${m.q.mood === mood ? "border-[#F59E0B] bg-[#D97706]/10" : "border-[#2D3442] hover:border-[#D97706]/50"}`}>
            <button onClick={() => mix(mood)} className="flex min-w-0 flex-1 items-center gap-1 px-2 py-1.5 text-left text-[#E8DFD1]" data-testid={`music-mood-${mood}`}>
              <Sparkles className={`h-3 w-3 shrink-0 ${m.q.mood === mood ? "text-[#F59E0B]" : "text-[#7A7468]"}`} /><span className="truncate">{mood}</span><span className="ml-auto text-[10px] text-[#7A7468]">{count(mood)}</span>
            </button>
            {m.customMoods.includes(mood) && <button onClick={() => window.confirm(`Delete the “${mood}” mood? Songs keep their other moods.`) && m.moodOps.removeCustom(mood)} title="Delete this mood" className="px-1 text-[#7A7468] hover:text-[#F87171]" data-testid={`music-mood-delete-${mood}`}>×</button>}
            {count(mood) > 0 && <AddToPlaylist m={m} paths={moodPaths(m, mood)} testid={`music-mood-save-${mood}`} />}
          </div>
        ))}
      </div>
      <NewMood m={m} />
      <p className="text-[10px] leading-relaxed text-[#7A7468]">To change a song's mood, use the tag icon next to it in the Library (or select many songs). Sorting never changes moods you've set yourself. Sorting sends only song file names to our AI, and a mood suggestion sends the last few paragraphs you wrote. Your music files never leave this computer.</p>
    </div>
  );
};
